import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  PhoneMissed as PhoneMissedIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import ContactApiService from '../services/ContactApiService';
import callService from '../services/CallService';
import servicesService from '../services/ServicesService';
import appConfig from '../shared/js/app-config';
import CallButton from '../components/CallButton';
import useIncomingCall from '../hooks/useIncomingCall';
import IncomingCallNotification from '../components/IncomingCallNotification';

const FILTERS = [
  { value: 'all', label: 'All calls' },
  { value: 'missed', label: 'Missed' },
  { value: 'attended', label: 'Attended' },
];

function formatDate(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function getStatusChip(status, callType) {
  if (callType === 'missed' || status === 'MISSED') {
    return { label: 'Missed', color: 'error' };
  }
  if (callType === 'outgoing') {
    return { label: 'Outgoing', color: 'primary' };
  }
  if (callType === 'incoming') {
    return { label: 'Incoming', color: 'success' };
  }
  const map = {
    MISSED: { label: 'Missed', color: 'error' },
    COMPLETED: { label: 'Completed', color: 'success' },
    REJECTED: { label: 'Rejected', color: 'warning' },
    FAILED: { label: 'Failed', color: 'error' },
    TERMINATED_BY_BUSINESS: { label: 'Ended', color: 'default' },
    RINGING: { label: 'Ringing', color: 'info' },
  };
  return map[status] || { label: status || 'Unknown', color: 'default' };
}

function getChannelLabel(channel) {
  if (channel === 'phone') return 'Phone';
  return 'WhatsApp';
}

function getLogTimestamp(log) {
  const ts = log.initiated_at || log.started_at || log.created_at;
  return ts ? new Date(ts).getTime() : 0;
}

function sortLogsByInitiateTime(logs) {
  return [...logs].sort((a, b) => getLogTimestamp(b) - getLogTimestamp(a));
}

function getDirectionIcon(direction, status) {
  const isMissed = status === 'MISSED';
  const color = isMissed ? '#e53935' : '#43a047';
  if (direction === 'BUSINESS_INITIATED') {
    return <CallMadeIcon sx={{ fontSize: 18, color }} />;
  }
  if (isMissed) {
    return <PhoneMissedIcon sx={{ fontSize: 18, color }} />;
  }
  return <CallReceivedIcon sx={{ fontSize: 18, color }} />;
}

export default function CallingView() {
  const serviceId = appConfig.getServiceId();
  const [phoneNumberId, setPhoneNumberId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [logFilter, setLogFilter] = useState('all');
  const [callLogs, setCallLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsRefreshKey, setLogsRefreshKey] = useState(0);

  const { incomingCall, clearIncomingCall } = useIncomingCall(serviceId, !!phoneNumberId);

  useEffect(() => {
    servicesService.getWhatsapp()
      .then((whatsapp) => {
        if (whatsapp?.phoneNumberId) {
          setPhoneNumberId(whatsapp.phoneNumberId);
        }
      })
      .catch(() => setPhoneNumberId(null));
  }, [serviceId]);

  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const res = await ContactApiService.getContactsDetails({ limit: 500, search: searchQuery });
      setContacts(res?.data?.contacts || []);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(fetchContacts, searchQuery ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchContacts, searchQuery]);

  const fetchCallLogs = useCallback(async () => {
    if (!serviceId) return;
    setLogsLoading(true);
    try {
      const logs = await callService.getServiceCallLogs(serviceId, logFilter, 200);
      setCallLogs(sortLogsByInitiateTime(Array.isArray(logs) ? logs : []));
    } catch {
      setCallLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [serviceId, logFilter, logsRefreshKey]);

  useEffect(() => {
    fetchCallLogs();
  }, [fetchCallLogs]);

  const filteredContacts = contacts.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const name = (c.attrs?.name || c.fullName || '').toLowerCase();
    const phone = (c.attrs?.phoneNumber || c.phoneNumber || '').toLowerCase();
    return name.includes(q) || phone.includes(q);
  });

  const handleCallEnded = () => {
    setLogsRefreshKey((k) => k + 1);
  };

  const handleContactClick = (contact) => {
    const id = contact.attrs?.id;
    if (selectedContact?.attrs?.id === id) {
      setSelectedContact(null);
    } else {
      setSelectedContact(contact);
    }
  };

  const selectContactFromLog = (log) => {
    if (!log.contact_id) return;
    if (selectedContact?.attrs?.id === log.contact_id) {
      setSelectedContact(null);
      return;
    }
    const match = contacts.find((c) => c.attrs?.id === log.contact_id);
    if (match) {
      setSelectedContact(match);
      return;
    }
    setSelectedContact({
      attrs: {
        id: log.contact_id,
        name: log.contact_name || log.contact_phone || 'Unknown',
        phoneNumber: log.contact_phone,
      },
    });
  };

  const displayLogs = useMemo(() => {
    if (!selectedContact?.attrs?.id) {
      return callLogs;
    }
    return callLogs.filter((log) => log.contact_id === selectedContact.attrs.id);
  }, [callLogs, selectedContact]);

  return (
    <Box sx={{ display: 'flex', height: '100%', bgcolor: '#e9edef', overflow: 'hidden' }}>
      {/* Contacts panel */}
      <Box
        sx={{
          width: { xs: '100%', md: 320 },
          minWidth: { md: 280 },
          borderRight: '1px solid #d1d7db',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #eef2f7' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
            Call a contact
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
          {contactsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredContacts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No contacts found
            </Typography>
          ) : (
            filteredContacts.map((contact) => {
              const id = contact.attrs?.id;
              const name = contact.attrs?.name || contact.fullName || 'Unknown';
              const phone = contact.attrs?.phoneNumber || contact.phoneNumber || '';
              const isSelected = selectedContact?.attrs?.id === id;
              return (
                <ListItemButton
                  key={id}
                  selected={isSelected}
                  onClick={() => handleContactClick(contact)}
                  sx={{ py: 1.25 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40 }}>
                      {name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={name}
                    secondary={phone}
                    primaryTypographyProps={{ fontWeight: isSelected ? 600 : 400, noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              );
            })
          )}
        </List>
      </Box>

      {/* Main panel */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Selected contact + call action */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderBottom: '1px solid #d1d7db',
            bgcolor: '#f0f2f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {selectedContact ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: '#1976d2' }}>
                {(selectedContact.attrs?.name || '?').charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography fontWeight={600}>{selectedContact.attrs?.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedContact.attrs?.phoneNumber}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">Select a contact to start a call</Typography>
          )}

          {selectedContact && (
            <CallButton
              serviceId={serviceId}
              contactId={selectedContact.attrs?.id}
              phoneNumber={selectedContact.attrs?.phoneNumber}
              contactName={selectedContact.attrs?.name}
              variant="button"
              onCallEnded={handleCallEnded}
            />
          )}
        </Paper>

        {/* Call logs */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#fff' }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #eef2f7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Call history
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedContact
                  ? `Showing calls with ${selectedContact.attrs?.name}`
                  : 'All calls for this service'}
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={logFilter}
                label="Filter"
                onChange={(e) => setLogFilter(e.target.value)}
              >
                {FILTERS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={32} />
              </Box>
            ) : displayLogs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                {selectedContact
                  ? 'No calls with this contact'
                  : 'No call history yet'}
              </Typography>
            ) : (
              <List disablePadding>
                {displayLogs.map((log) => {
                  const status = log.call_status || log.status;
                  const callType = log.call_type;
                  const chip = getStatusChip(status, callType);
                  const name = log.contact_name || log.contact_phone || log.phone_number || 'Unknown number';
                  return (
                    <ListItemButton
                      key={log.call_id || log.id}
                      onClick={() => selectContactFromLog(log)}
                      sx={{
                        borderBottom: '1px solid #f1f5f9',
                        py: 1.5,
                        px: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        <Box sx={{ minWidth: 28 }}>
                          {getDirectionIcon(log.direction, status)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={600} noWrap>{name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                            <Chip
                              label={getChannelLabel(log.call_channel)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                            <AccessTimeIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(log.initiated_at || log.started_at || log.created_at)}
                            </Typography>
                            {log.duration > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                · {formatDuration(log.duration)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Chip label={chip.label} color={chip.color} size="small" variant="outlined" />
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>
      </Box>

      {incomingCall && phoneNumberId && (
        <IncomingCallNotification
          incomingCall={incomingCall}
          onClear={() => {
            clearIncomingCall();
            handleCallEnded();
          }}
          serviceId={serviceId}
          phoneNumberId={phoneNumberId}
        />
      )}
    </Box>
  );
}
